import React, { useEffect, useState } from "react";
import axios from "axios";

// Phase 25.16r — Homepage signal strip.
//
// Reads the current signal counts from the public /api/ticker endpoint
// (admin-editable from /app/admin/system) and displays them as a thin
// amber strip in the homepage hero. Designed to signal velocity to
// any visitor — operator, investor, competitor — WITHOUT revealing
// specific names or playbook details.
//
// Numbers in: verticals · prospects · named_targets · pilots_live
// Plus a label band that can be edited per campaign cycle.
//
// Layout: horizontal strip · 4 cells · amber-on-black-tint · readable
// from across a room at presentation distance.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomepageSignalStrip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/ticker`).then((r) => {
      if (mounted) setData(r.data);
    }).catch(() => {
      // Quiet failure — strip simply doesn't render if backend is down.
      // Prevents homepage from getting a broken-state visual.
    });
    return () => { mounted = false; };
  }, []);

  if (!data) return null;

  const items = [
    { label: "VERTICALS", value: data.verticals },
    { label: "PROSPECTS · ACTIVE", value: data.prospects },
    { label: "NAMED TARGETS · IN PIPELINE", value: data.named_targets },
    { label: "PILOTS · LIVE", value: data.pilots_live, accent: true },
  ];

  return (
    <div
      className="border-t border-b border-[#FFB000]/30 bg-[#FFB000]/5"
      data-testid="homepage-signal-strip"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB000]">
          <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
          <span>// {data.label}</span>
        </div>
        <div className="flex items-center gap-x-8 gap-y-2 flex-wrap">
          {items.map((it) => (
            <div
              key={it.label}
              className="flex items-baseline gap-2"
              data-testid={`ticker-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <span className={`font-heading font-black text-2xl sm:text-3xl tracking-tight ${it.accent ? "text-[#00E676]" : "text-white"}`}>
                {it.value}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">
                {it.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
